import type { RouteRecordRaw } from 'vue-router'

import { AppShellAsync } from './shellRegistry'
import PlaysPage from './views/PlaysPage.vue'
import SalesPlayDetailPage from './views/SalesPlayDetailPage.vue'
import SalesPlayVersionsPage from './views/SalesPlayVersionsPage.vue'
import SalesPlayIcpPage from './views/SalesPlayIcpPage.vue'
import SalesPlayPersonasPage from './views/SalesPlayPersonasPage.vue'
import SalesPlayEmailsPage from './views/SalesPlayEmailsPage.vue'
import SalesPlayCanvasPage from './views/SalesPlayCanvasPage.vue'
import SalesPlaySubpageStub from './views/SalesPlaySubpageStub.vue'
import AccountCandidatesPage from './views/AccountCandidatesPage.vue'
import ContactCandidatesPage from './views/ContactCandidatesPage.vue'
import SequenceDetailPage from './views/SequenceDetailPage.vue'
import AgentManagePage from './views/AgentManagePage.vue'
import ActionsPage from './views/ActionsPage.vue'
import ActionDetailPage from './views/ActionDetailPage.vue'
import ActivityPage from './views/ActivityPage.vue'
import EngagedContactsPage from './views/EngagedContactsPage.vue'
import AgentRunDetailPage from './views/AgentRunDetailPage.vue'
import AccountDetailPage from './views/AccountDetailPage.vue'
import ContactDetailPage from './views/ContactDetailPage.vue'
import AccountsListPage from './views/AccountsListPage.vue'
import ContactsListPage from './views/ContactsListPage.vue'
import HumanDialerQueuePage from './views/HumanDialerQueuePage.vue'
import DialerHistoryPage from './views/DialerHistoryPage.vue'
import CanvasPage from './views/CanvasPage.vue'
import ReportsPage from './views/ReportsPage.vue'

// Settings & admin pages (next-native copies of classic pages)
import IntegrationsPage from './views/IntegrationsPage.vue'
import EmailSettingsPage from './views/EmailSettingsPage.vue'
import AgentSettingsPage from './views/AgentSettingsPage.vue'
import AboutUsPage from './views/AboutUsPage.vue'
import IcpPersonasPage from './views/IcpPersonasPage.vue'
import UsagePage from './views/UsagePage.vue'
import UsersPage from './views/UsersPage.vue'
import CustomerListPage from './views/CustomerListPage.vue'
import PhoneSettingsPage from './views/PhoneSettingsPage.vue'
import SalesPlaySettingsPage from './views/SalesPlaySettingsPage.vue'
import ProfilePage from './views/ProfilePage.vue'

/**
 * Route table active when ``ui_mode === 'next'``. All routes are
 * children of a single AppShell-rooted layout route so the shell
 * (topbar + chat panel + context drawer) stays mounted across
 * navigation. Pages render only their page-level content; the chat
 * draft, chat history, and drawer state all survive route changes.
 *
 * URLs and route names are unchanged from the pre-layout structure;
 * absolute child paths produce identical URLs.
 */
export const nextRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    component: AppShellAsync,
    children: [
      { path: '', redirect: '/home' },
      {
        path: '/home',
        component: () => import('../r3/R3Scope.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'home',
            component: () => import('../r3/pages/home/HomeEntry.vue'),
            meta: { requiresAuth: true, title: 'Overview' },
          },
        ],
      },
      {
        path: '/plays',
        name: 'plays',
        component: PlaysPage,
        meta: { requiresAuth: true, title: 'Plays' },
      },
      {
        path: '/sales-plays/:id',
        name: 'sales-play-canvas',
        component: SalesPlayCanvasPage,
        meta: { requiresAuth: true, title: 'Sales Play' },
      },
      {
        path: '/sales-plays/:id/canvas',
        redirect: (to) => `/sales-plays/${to.params.id}`,
      },
      {
        path: '/sales-plays/:id/details',
        name: 'sales-play-detail',
        component: SalesPlayDetailPage,
        meta: { requiresAuth: true, title: 'Sales Play Details' },
      },
      {
        path: '/sales-plays/:id/settings',
        name: 'sales-play-settings',
        component: SalesPlaySettingsPage,
        meta: { requiresAuth: true, title: 'Play Settings' },
      },
      {
        path: '/sales-plays/:id/versions',
        name: 'sales-play-versions',
        component: SalesPlayVersionsPage,
        meta: { requiresAuth: true, title: 'Sales Play Versions' },
      },
      {
        path: '/sales-plays/:id/agent',
        name: 'sales-play-agent',
        component: AgentManagePage,
        meta: { requiresAuth: true, title: 'Agent' },
      },
      {
        path: '/sales-plays/:id/sequence',
        name: 'sales-play-sequence',
        component: SequenceDetailPage,
        meta: { requiresAuth: true, title: 'Sequence' },
      },
      {
        path: '/sales-plays/:id/icp',
        name: 'sales-play-icp',
        component: SalesPlayIcpPage,
        meta: { requiresAuth: true, title: 'ICP' },
      },
      {
        path: '/sales-plays/:id/personas',
        name: 'sales-play-personas',
        component: SalesPlayPersonasPage,
        meta: { requiresAuth: true, title: 'Personas' },
      },
      {
        path: '/sales-plays/:id/candidates/companies',
        name: 'sales-play-candidates-companies',
        component: AccountCandidatesPage,
        meta: { requiresAuth: true, title: 'Companies' },
      },
      {
        path: '/sales-plays/:id/candidates/people',
        name: 'sales-play-candidates-people',
        component: ContactCandidatesPage,
        meta: { requiresAuth: true, title: 'People' },
      },
      {
        path: '/sales-plays/:id/emails',
        name: 'sales-play-emails',
        component: SalesPlayEmailsPage,
        meta: { requiresAuth: true, title: 'Sample emails' },
      },
      {
        path: '/sales-plays/:id/activity',
        name: 'sales-play-activity',
        component: ActivityPage,
        meta: { requiresAuth: true, title: 'Activity' },
      },
      {
        path: '/strategy',
        component: () => import('../r3/R3Scope.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'strategy',
            component: () => import('../r3/pages/strategy/StrategyPage.vue'),
            meta: { requiresAuth: true, title: 'Strategy' },
          },
          {
            path: 'runs/:runId',
            name: 'strategy-run',
            component: () => import('../r3/pages/strategy/StrategyPage.vue'),
            meta: { requiresAuth: true, title: 'Strategy run' },
          },
          { path: 'plays', redirect: '/strategy' },
        ],
      },
      {
        path: '/actions',
        name: 'actions',
        component: ActionsPage,
        meta: { requiresAuth: true, title: 'Actions' },
      },
      {
        path: '/actions/:reachoutId',
        name: 'action-detail',
        component: ActionDetailPage,
        meta: { requiresAuth: true, title: 'Action' },
      },
      {
        path: '/activity',
        name: 'activity',
        component: ActivityPage,
        meta: { requiresAuth: true, title: 'Activity' },
      },
      {
        path: '/engaged',
        name: 'engaged-contacts',
        component: EngagedContactsPage,
        meta: { requiresAuth: true, title: 'Engaged' },
      },
      {
        path: '/insights',
        name: 'insights',
        component: CanvasPage,
        meta: { requiresAuth: true, title: 'Insights' },
      },
      {
        path: '/reports/:tab?',
        name: 'reports',
        component: ReportsPage,
        meta: { requiresAuth: true, title: 'Reports' },
      },
      {
        path: '/dialer/human',
        name: 'human-dialer',
        component: HumanDialerQueuePage,
        meta: { requiresAuth: true, title: 'Dialer' },
      },
      {
        path: '/dialer/history',
        name: 'dialer-history',
        component: DialerHistoryPage,
        meta: { requiresAuth: true, title: 'Dialer history' },
      },
      {
        path: '/accounts',
        name: 'accounts-list',
        component: AccountsListPage,
        meta: { requiresAuth: true, title: 'Accounts' },
      },
      {
        path: '/accounts/:id',
        name: 'account-detail',
        component: AccountDetailPage,
        meta: { requiresAuth: true, title: 'Account' },
      },
      {
        path: '/contacts',
        name: 'contacts-list',
        component: ContactsListPage,
        meta: { requiresAuth: true, title: 'Contacts' },
      },
      {
        path: '/contacts/:id',
        name: 'contact-detail',
        component: ContactDetailPage,
        meta: { requiresAuth: true, title: 'Contact' },
      },
      {
        path: '/agent-runs/:id',
        name: 'agent-run-detail',
        component: AgentRunDetailPage,
        meta: { requiresAuth: true, title: 'Agent Run' },
      },
      // ── Settings & admin ──────────────────────────────────────
      {
        path: '/integrations',
        name: 'integrations',
        component: IntegrationsPage,
        meta: { requiresAuth: true, title: 'Integrations' },
      },
      // Folded into the per-CRM integration drawer (Integrations page). Redirect stale links/bookmarks.
      {
        path: '/crm-settings',
        redirect: '/integrations',
      },
      {
        path: '/activity-mappings',
        redirect: '/integrations',
      },
      {
        path: '/email-settings/:tab?',
        name: 'email-settings',
        component: EmailSettingsPage,
        meta: { requiresAuth: true, title: 'Email Settings' },
      },
      {
        path: '/agent-guardrails',
        name: 'agent-guardrails',
        component: AgentSettingsPage,
        meta: { requiresAuth: true, title: 'Agent Settings' },
      },
      {
        path: '/settings/phone',
        name: 'phone-settings',
        component: PhoneSettingsPage,
        meta: { requiresAuth: true, title: 'Phone Settings' },
      },
      {
        path: '/about',
        name: 'about',
        component: AboutUsPage,
        meta: { requiresAuth: true, title: 'About' },
      },
      {
        path: '/icp-personas',
        name: 'icp-personas',
        component: IcpPersonasPage,
        meta: { requiresAuth: true, title: 'ICP Personas' },
      },
      {
        path: '/usage',
        name: 'usage',
        component: UsagePage,
        meta: { requiresAuth: true, title: 'Usage' },
      },
      {
        path: '/settings/users',
        name: 'users',
        component: UsersPage,
        meta: { requiresAuth: true, title: 'Users' },
      },
      {
        path: '/settings/customer-list',
        name: 'customer-list',
        component: CustomerListPage,
        meta: { requiresAuth: true, title: 'Customer List' },
      },
      {
        path: '/profile',
        name: 'profile',
        component: ProfilePage,
        meta: { requiresAuth: true, title: 'Profile' },
      },
      { path: '/unsubscribes', redirect: '/email-settings/unsubscribes' },
      { path: ':pathMatch(.*)*', redirect: '/home' },
    ],
  },
]
